import { useState } from 'react';
import {
  PiArrowSquareOut,
  PiCaretDown,
  PiCaretUp,
  PiCheckCircle,
  PiChatCircleText,
  PiWarningCircle,
} from 'react-icons/pi';
import {
  type EmployeeAssistance,
  type EmployeeExecutionAdvisory,
  type EmployeeExecutionBlocker,
} from 'src/api/generated';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Card } from 'src/modules/shared/components/atoms/cards';
import { CardBody } from 'src/modules/shared/components/atoms/cards/card-body';
import { CardHeader } from 'src/modules/shared/components/atoms/cards/card-header';
import { StatusPill } from 'src/modules/shared/components/atoms/status-pill';
import { Text } from 'src/modules/shared/components/atoms/text';
import { MetaRow } from 'src/modules/shared/components/molecules/meta-row';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useIsElementTruncated } from 'src/modules/shared/hooks/use-is-element-truncated';
import { AiRobotOutlineIcon } from 'src/modules/shared/components/atoms/indicators/ai-badge';
import { getEmployeeAssistancePresentation } from 'src/modules/automations/utils/employees/employee-assistance-presentation.util';
import type { EmployeeDisplayIdentity } from 'src/modules/automations/types/employees/employee-display-identity.type';
import type { ComponentVariant } from 'src/modules/shared/types/component.type';

const CATEGORY_VARIANTS: Record<
  EmployeeExecutionBlocker['category'] | EmployeeExecutionAdvisory['category'],
  ComponentVariant
> = {
  'missing-capability': 'danger',
  'configuration-unavailable': 'warning',
  'terminal-tool-failure': 'danger',
  'repeated-tool-failure': 'warning',
  'terminal-operation-failure': 'warning',
  'message-fidelity-review': 'warning',
};

interface EmployeeAssistanceCardProps {
  /** Assistance record to display. */
  assistance: EmployeeAssistance;
  /** Resolved human-facing identity for the AI Agent. */
  agentIdentity: EmployeeDisplayIdentity;
  /** Opens the source execution. */
  onOpenExecution?: () => void;
  /** Opens the CRM conversation when the record has its full location. */
  onOpenConversation?: () => void;
  /** Dismisses an open assistance after human review. */
  onDismiss?: () => void;
  /** Whether the dismissal request is active. */
  isDismissing?: boolean;
}

/**
 * Renders a responsive AI Agent assistance summary with source navigation.
 *
 * @param props - Assistance record, agent identity, and navigation callbacks.
 * @returns The assistance summary card.
 */
export const EmployeeAssistanceCard: React.FC<EmployeeAssistanceCardProps> = ({
  assistance,
  agentIdentity,
  onOpenExecution,
  onOpenConversation,
  onDismiss,
  isDismissing = false,
}) => {
  const { language, t } = useI18n();
  const [objectiveExpanded, setObjectiveExpanded] = useState(false);
  const { ref: objectiveRef, isTruncated: objectiveIsTruncated } =
    useIsElementTruncated<HTMLDivElement>();
  const isOpen = !('resolvedAt' in assistance);
  const statusLabel = isOpen
    ? t('automations.aiAgents.assistance.status.open')
    : t('automations.aiAgents.assistance.status.resolved');
  const presentation = getEmployeeAssistancePresentation(assistance, t);
  const updatedAt = new Intl.DateTimeFormat(language, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(assistance.updatedAt));
  const showObjectiveToggle = objectiveExpanded || objectiveIsTruncated;
  const category =
    'blocker' in assistance
      ? assistance.blocker.category
      : assistance.advisory.category;
  const categoryVariant = CATEGORY_VARIANTS[category];

  return (
    <Card
      appearance="outline"
      shadow="none"
      className="overflow-hidden bg-white"
    >
      <CardHeader className="border-neutral-100 bg-slate-50/70">
        <MetaRow
          left={
            <>
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                <PiWarningCircle className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <div
                  ref={objectiveRef}
                  className={objectiveExpanded ? '' : 'overflow-hidden'}
                >
                  <Text
                    weight="medium"
                    className={`${objectiveExpanded ? 'whitespace-normal' : 'truncate'} text-slate-900`}
                  >
                    {assistance.objective}
                  </Text>
                </div>
                <Text size="tiny" color="secondary" className="mt-0.5">
                  {updatedAt}
                </Text>
              </div>
              {showObjectiveToggle ? (
                <Button
                  type="button"
                  size="xs"
                  variant="neutral"
                  appearance="ghost"
                  isIconOnly
                  aria-expanded={objectiveExpanded}
                  aria-label={t(
                    objectiveExpanded
                      ? 'automations.aiAgents.assistance.collapseObjective'
                      : 'automations.aiAgents.assistance.expandObjective'
                  )}
                  onClick={() => setObjectiveExpanded((expanded) => !expanded)}
                  className="shrink-0 text-slate-500"
                >
                  {objectiveExpanded ? (
                    <PiCaretUp className="h-4 w-4" />
                  ) : (
                    <PiCaretDown className="h-4 w-4" />
                  )}
                </Button>
              ) : null}
            </>
          }
          right={
            <StatusPill
              label={statusLabel}
              variant={isOpen ? 'neutral' : 'success'}
              appearance="soft"
            />
          }
        />
      </CardHeader>

      <CardBody className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <StatusPill
              label={presentation.categoryLabel}
              variant={categoryVariant}
              appearance="soft"
              size="xs"
            />
            <span className="flex min-w-0 items-center gap-1.5 text-slate-600">
              <AiRobotOutlineIcon className="h-4 w-4 shrink-0" />
              <Text size="small" className="truncate">
                {agentIdentity.name} · {agentIdentity.identifier}
              </Text>
            </span>
          </div>

          <div className="flex shrink-0 flex-col gap-2 sm:ml-auto sm:flex-row">
            {isOpen && onDismiss ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                appearance="ghost"
                loading={isDismissing}
                disabled={isDismissing}
                onClick={onDismiss}
                aria-label={t('automations.aiAgents.assistance.dismissLabel')}
                className="w-full gap-1.5 sm:w-auto"
              >
                <PiCheckCircle className="h-4 w-4" />
                {t('automations.aiAgents.assistance.dismiss')}
              </Button>
            ) : null}
            {assistance.conversationId && onOpenConversation ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                appearance="outline"
                onClick={onOpenConversation}
                aria-label={t(
                  'automations.aiAgents.assistance.openConversationLabel',
                  { conversationId: assistance.conversationId }
                )}
                className="w-full gap-1.5 sm:w-auto"
              >
                <PiChatCircleText className="h-4 w-4" />
                {t('automations.aiAgents.assistance.openConversation', {
                  conversationId: assistance.conversationId,
                })}
              </Button>
            ) : null}
            {onOpenExecution ? (
              <Button
                type="button"
                size="sm"
                variant="primary"
                appearance="soft"
                onClick={onOpenExecution}
                className="w-full gap-2 sm:w-auto"
              >
                <PiArrowSquareOut className="h-4 w-4" />
                {t('automations.aiAgents.assistance.openExecution')}
              </Button>
            ) : null}
          </div>
        </div>

        <div className="rounded-lg bg-slate-50 p-3.5">
          <Text size="small" weight="medium" className="text-slate-800">
            {presentation.reason}
          </Text>
          <div className="mt-1.5">
            <Text size="small" color="secondary">
              {presentation.context}
            </Text>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};
